// Composer blocks CRUD + per-workspace category order + draft fragment composer.
// Wave 1 (2026-05-09): backed by `composer_blocks` table with lazy per-workspace
// seeding from DEFAULT_SEED_BLOCKS. See mem://features/composer-saturday-plan.md.

import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import {
  BLOCK_TYPE_ORDER,
  DEFAULT_SEED_BLOCKS,
  type ComposerBlock,
  type ComposerBlockImageAlign,
  type ComposerBlockImagePosition,
  type ComposerBlockImageSize,
  type ComposerBlockRow,
  type ComposerBlockType,
} from "./types";
import { BLOCK_IMAGES_BUCKET, refreshBlockImageUrl } from "./images";
import {
  looksLikeHtml,
  plainTextToBlockHtml,
  blockHtmlToPlainText,
} from "./sanitizeBlockHtml";

const rowToBlock = (r: ComposerBlockRow): ComposerBlock => ({
  id: r.id,
  type: r.type as ComposerBlockType,
  title: r.title,
  body: r.body,
  signatureName: (r as unknown as { signature_name: string | null }).signature_name ?? null,
  imageUrl: (r as unknown as { image_url: string | null }).image_url ?? null,
  imagePath: (r as unknown as { image_path: string | null }).image_path ?? null,
  imageAlt: (r as unknown as { image_alt: string | null }).image_alt ?? null,
  imagePosition: ((r as unknown as { image_position: string | null }).image_position ?? null) as ComposerBlockImagePosition | null,
  imageSize: ((r as unknown as { image_size: string | null }).image_size ?? null) as ComposerBlockImageSize | null,
  imageAlign: ((r as unknown as { image_align: string | null }).image_align ?? null) as ComposerBlockImageAlign | null,
});

export const listBlocks = async (workspaceId: string): Promise<ComposerBlock[]> => {
  const { data, error } = await supabase
    .from("composer_blocks")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("type", { ascending: true })
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw new Error(error.message);
  const blocks = (data ?? []).map(rowToBlock);
  // Refresh signed URLs from imagePath since the bucket is private (2026-05-27).
  await Promise.all(
    blocks.map(async (b) => {
      if (b.imagePath) {
        const fresh = await refreshBlockImageUrl(b.imagePath);
        if (fresh) b.imageUrl = fresh;
      }
    }),
  );
  return blocks;
};

type ImageFields = {
  imageUrl?: string | null;
  imagePath?: string | null;
  imageAlt?: string | null;
  imagePosition?: ComposerBlockImagePosition | null;
  imageSize?: ComposerBlockImageSize | null;
  imageAlign?: ComposerBlockImageAlign | null;
};

const imageFieldsToRow = (type: ComposerBlockType | undefined, v: ImageFields): Record<string, unknown> => {
  // Only signature, author_bio, and product_cta blocks may carry image fields.
  const allowsImage = type === undefined
    || type === "signature"
    || type === "author_bio"
    || type === "product_cta";
  if (!allowsImage) {
    return {
      image_url: null,
      image_path: null,
      image_alt: null,
      image_position: null,
      image_size: null,
      image_align: null,
    };
  }

  const out: Record<string, unknown> = {};
  if (v.imageUrl !== undefined) out.image_url = v.imageUrl;
  if (v.imagePath !== undefined) out.image_path = v.imagePath;
  if (v.imageAlt !== undefined) out.image_alt = v.imageAlt;
  if (v.imagePosition !== undefined) out.image_position = v.imagePosition;
  if (v.imageSize !== undefined) out.image_size = v.imageSize;
  if (v.imageAlign !== undefined) out.image_align = v.imageAlign;
  return out;
};

export const createBlock = async (
  workspaceId: string,
  values: { type: ComposerBlockType; title: string; body: string; signatureName?: string | null } & ImageFields,
): Promise<ComposerBlock> => {
  const { data: u } = await supabase.auth.getUser();
  const payload = {
    workspace_id: workspaceId,
    type: values.type,
    title: values.title,
    body: values.body,
    signature_name: values.type === "signature" ? (values.signatureName ?? null) : null,
    created_by_user_id: u.user?.id ?? null,
    ...imageFieldsToRow(values.type, values),
  } as unknown as TablesInsert<"composer_blocks">;
  const { data, error } = await supabase
    .from("composer_blocks")
    .insert(payload)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return rowToBlock(data);
};

export const updateBlock = async (
  id: string,
  values: { title?: string; body?: string; type?: ComposerBlockType; sort_order?: number; signatureName?: string | null } & ImageFields,
): Promise<ComposerBlock> => {
  const { title, body, type, sort_order, signatureName } = values;
  const patch = {
    ...(title !== undefined ? { title } : {}),
    ...(body !== undefined ? { body } : {}),
    ...(type !== undefined ? { type } : {}),
    ...(sort_order !== undefined ? { sort_order } : {}),
    // signature_name only applies to signature blocks; clear it otherwise.
    ...(signatureName !== undefined || type !== undefined
      ? { signature_name: type === "signature" ? (signatureName ?? null) : null }
      : {}),
    ...imageFieldsToRow(type, values),
  } as unknown as TablesUpdate<"composer_blocks">;
  const { data, error } = await supabase
    .from("composer_blocks")
    .update(patch)
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw new Error(error.message);
  return rowToBlock(data);
};

export const deleteBlock = async (id: string): Promise<void> => {
  // Best-effort cleanup of attached image first.
  try {
    const { data } = await supabase
      .from("composer_blocks")
      .select("image_path")
      .eq("id", id)
      .maybeSingle();
    const path = (data as { image_path?: string | null } | null)?.image_path ?? null;
    if (path) await supabase.storage.from(BLOCK_IMAGES_BUCKET).remove([path]);
  } catch {
    // non-fatal
  }
  const { error } = await supabase.from("composer_blocks").delete().eq("id", id);
  if (error) throw new Error(error.message);
};

/**
 * Lazily seed a workspace with DEFAULT_SEED_BLOCKS on first panel open.
 * Idempotent: skips slugs that already exist (matched on metadata.seed_slug).
 */
export const ensureSeedBlocks = async (workspaceId: string): Promise<void> => {
  const { data: existing, error: selErr } = await supabase
    .from("composer_blocks")
    .select("metadata")
    .eq("workspace_id", workspaceId);
  if (selErr) throw new Error(selErr.message);
  const present = new Set<string>();
  for (const r of existing ?? []) {
    const md = (r.metadata as Record<string, unknown> | null) ?? null;
    const slug = md && typeof md.seed_slug === "string" ? (md.seed_slug as string) : null;
    if (slug) present.add(slug);
  }
  const missing = DEFAULT_SEED_BLOCKS.filter((s) => !present.has(s.seed_slug));
  if (missing.length === 0) return;
  const { data: u } = await supabase.auth.getUser();
  const rows: TablesInsert<"composer_blocks">[] = missing.map((s, i) => ({
    workspace_id: workspaceId,
    type: s.type,
    title: s.title,
    body: s.body,
    sort_order: i,
    created_by_user_id: u.user?.id ?? null,
    metadata: { seed_slug: s.seed_slug },
  }));
  const { error } = await supabase.from("composer_blocks").insert(rows);
  if (error) throw new Error(error.message);
};

/**
 * Re-insert any DEFAULT_SEED_BLOCKS whose seed_slug is missing from the workspace.
 * Existing blocks are untouched. Returns the number of blocks restored.
 */
export const restoreMissingSeedBlocks = async (workspaceId: string): Promise<number> => {
  const { data: existing, error: selErr } = await supabase
    .from("composer_blocks")
    .select("metadata")
    .eq("workspace_id", workspaceId);
  if (selErr) throw new Error(selErr.message);
  const present = new Set<string>();
  for (const r of existing ?? []) {
    const md = (r.metadata as Record<string, unknown> | null) ?? null;
    const slug = md && typeof md.seed_slug === "string" ? (md.seed_slug as string) : null;
    if (slug) present.add(slug);
  }
  const missing = DEFAULT_SEED_BLOCKS.filter((s) => !present.has(s.seed_slug));
  if (missing.length === 0) return 0;
  const { data: u } = await supabase.auth.getUser();
  const rows: TablesInsert<"composer_blocks">[] = missing.map((s, i) => ({
    workspace_id: workspaceId,
    type: s.type,
    title: s.title,
    body: s.body,
    sort_order: 9999 + i,
    created_by_user_id: u.user?.id ?? null,
    metadata: { seed_slug: s.seed_slug },
  }));
  const { error } = await supabase.from("composer_blocks").insert(rows);
  if (error) throw new Error(error.message);
  return missing.length;
};

// ---------------------------------------------------------------------------
// Per-workspace category order for the Blocks panel accordions.
// Backed by `composer_block_category_order` (workspace_id PK + text[]).
// ---------------------------------------------------------------------------

const sanitizeCategoryOrder = (raw: unknown): ComposerBlockType[] => {
  const seen = new Set<ComposerBlockType>();
  const out: ComposerBlockType[] = [];
  if (Array.isArray(raw)) {
    for (const v of raw) {
      if (typeof v === "string" && (BLOCK_TYPE_ORDER as string[]).includes(v)) {
        const t = v as ComposerBlockType;
        if (!seen.has(t)) { seen.add(t); out.push(t); }
      }
    }
  }
  for (const t of BLOCK_TYPE_ORDER) {
    if (!seen.has(t)) out.push(t);
  }
  return out;
};

export const getCategoryOrder = async (workspaceId: string): Promise<ComposerBlockType[]> => {
  const { data, error } = await supabase
    .from("composer_block_category_order")
    .select("category_order")
    .eq("workspace_id", workspaceId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return sanitizeCategoryOrder(data?.category_order);
};

export const saveCategoryOrder = async (
  workspaceId: string,
  order: ComposerBlockType[],
): Promise<void> => {
  const clean = sanitizeCategoryOrder(order);
  const { error } = await supabase
    .from("composer_block_category_order")
    .upsert(
      { workspace_id: workspaceId, category_order: clean },
      { onConflict: "workspace_id" },
    );
  if (error) throw new Error(error.message);
};

export const blocksByType = (blocks: ComposerBlock[]) => {
  const map = new Map<ComposerBlockType, ComposerBlock[]>();
  for (const t of BLOCK_TYPE_ORDER) map.set(t, []);
  for (const b of blocks) map.get(b.type)?.push(b);
  return map;
};

/**
 * Build an ordered list of drop-fragments for inserting a block into a Draft
 * Canvas. Each fragment is either an HTML string (parsed against the live
 * editor schema by the drop handler) or a ProseMirror node (for the signature
 * image, which has no HTML representation in our schema).
 *
 * Order, for signature blocks with image+name:
 *   image (above|below|only) — signature name paragraph — body html
 *
 * For non-signature blocks: just body html.
 */
export type ComposerBlockDropFragment =
  | { kind: "html"; html: string }
  | { kind: "node"; node: unknown };

const bodyToHtmlFragment = (body: string): ComposerBlockDropFragment | null => {
  const raw = body ?? "";
  const html = looksLikeHtml(raw) ? raw : plainTextToBlockHtml(raw);
  const trimmed = html.replace(/<p>\s*(<br\s*\/?>)?\s*<\/p>/gi, "").trim();
  if (!trimmed) return null;
  return { kind: "html", html };
};

export const composeBlockDropFragments = (block: ComposerBlock): ComposerBlockDropFragment[] => {
  const typeAllowsImage = block.type === "signature" || block.type === "author_bio" || block.type === "product_cta";
  const hasImage = typeAllowsImage && !!block.imageUrl && !!block.imageAlt;
  const bodyFrag = bodyToHtmlFragment(block.body);

  // Quote blocks: wrap the body in <blockquote>.
  if (block.type === "quote" && bodyFrag && bodyFrag.kind === "html") {
    bodyFrag.html = `<blockquote>${bodyFrag.html}</blockquote>`;
  }

  // Signature name renders as its own paragraph (between image and body).
  const signatureName = (block.type === "signature" ? (block.signatureName ?? "").trim() : "");
  const nameFrag: ComposerBlockDropFragment | null = signatureName
    ? {
        kind: "html",
        html: `<p>${signatureName
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")}</p>`,
      }
    : null;

  if (!hasImage) {
    return [nameFrag, bodyFrag].filter((f): f is ComposerBlockDropFragment => !!f);
  }

  const imageFrag: ComposerBlockDropFragment = {
    kind: "node",
    node: {
      type: "excerptus.signatureImage",
      attrs: {
        src: block.imageUrl,
        alt: block.imageAlt,
        size: (block.imageSize ?? "medium") as ComposerBlockImageSize,
        align: (block.imageAlign ?? "left") as ComposerBlockImageAlign,
      },
    },
  };
  const pos = (block.imagePosition ?? "below") as ComposerBlockImagePosition;
  if (pos === "only") return [imageFrag];
  const rest = [nameFrag, bodyFrag].filter((f): f is ComposerBlockDropFragment => !!f);
  return pos === "above" ? [imageFrag, ...rest] : [...rest, imageFrag];
};

/** Plain-text serialization for the `text/plain` drag slot. */
export const composeBlockPlainText = (block: ComposerBlock): string => {
  const lines: string[] = [];
  if (block.type === "signature" && block.signatureName?.trim()) {
    lines.push(block.signatureName.trim());
  }
  const bodyText = looksLikeHtml(block.body) ? blockHtmlToPlainText(block.body) : (block.body ?? "");
  if (bodyText.trim()) lines.push(bodyText);
  return lines.join("\n\n");
};

/**
 * @deprecated Kept for back-compat with any external readers. New code should
 * call `composeBlockDropFragments`. The returned shape collapses every
 * fragment into a single { type: "doc", content: [...] } where html fragments
 * become a single paragraph of plain text (loses formatting).
 */
export const composeBlockDocFragment = (block: ComposerBlock): { type: "doc"; content: unknown[] } => {
  const frags = composeBlockDropFragments(block);
  const content: unknown[] = [];
  for (const f of frags) {
    if (f.kind === "node") {
      content.push(f.node);
    } else {
      const text = blockHtmlToPlainText(f.html);
      for (const para of text.split(/\n{2,}/)) {
        content.push({
          type: "paragraph",
          content: para.trim() ? [{ type: "text", text: para }] : [],
        });
      }
    }
  }
  return { type: "doc", content };
};
